<template>
  <div class="page about">
    <section class="about-hero">
      <div class="hero-content">
        <p class="eyebrow">{{ isEnglish ? 'About Nyenyezia Forex' : 'À propos de Nyenyezia Forex' }}</p>
        <h1>{{ isEnglish ? 'A practical currency exchange service built on clarity and trust.' : 'Un service de change pratique, clair et fondé sur la confiance.' }}</h1>
        <p>
          {{ isEnglish ? 'Nyenyezia Forex supports local and international customers with currency exchange, money transfer guidance, and clear rate information.' : 'Nyenyezia Forex accompagne une clientèle locale et internationale avec le change de devises, l’orientation pour les transferts et une information claire sur les taux.' }}
        </p>
        <router-link :to="localizedPath('/services')" class="cta-button">{{ isEnglish ? 'Explore Services' : 'Voir nos services' }}</router-link>
      </div>
    </section>

    <section class="intro-section">
      <div class="container intro-grid">
        <div>
          <p class="eyebrow">{{ isEnglish ? 'Who we are' : 'Qui nous sommes' }}</p>
          <h2>{{ isEnglish ? 'Simple support for real exchange needs' : 'Un accompagnement simple pour des besoins de change réels' }}</h2>
        </div>
        <div class="intro-copy">
          <p>
            {{ isEnglish ? 'Nyenyezia Forex helps customers understand available rates, compare buy and sell values, and prepare exchange or transfer requests with confidence.' : 'Nyenyezia Forex aide ses clients à comprendre les taux disponibles, comparer les valeurs d’achat et de vente, et préparer leurs demandes de change ou de transfert avec confiance.' }}
          </p>
          <p>
            {{ isEnglish ? 'The goal is direct: make the process easier, more transparent, and supported by a team that can answer practical questions before a transaction.' : 'L’objectif est simple : rendre le processus plus facile, plus transparent et accompagné par une équipe capable de répondre aux questions pratiques avant une opération.' }}
          </p>
        </div>
      </div>
    </section>

    <section class="mission-section">
      <div class="container">
        <div class="section-heading">
          <p class="eyebrow">{{ isEnglish ? 'Mission' : 'Mission' }}</p>
          <h2>{{ isEnglish ? 'Make exchange information clear before customers decide.' : 'Rendre l’information de change claire avant la décision du client.' }}</h2>
        </div>
        <div class="mission-card">
          <img src="@/assets/chart-8305514_1280.jpg" :alt="isEnglish ? 'Currency exchange service' : 'Service de change'" />
          <div>
            <h3>{{ isEnglish ? 'Transparency at each step' : 'Transparence à chaque étape' }}</h3>
            <p>
              {{ isEnglish ? 'Customers should be able to see current rates, understand the difference between buy and sell prices, and contact the team for confirmation before moving forward.' : 'Les clients doivent pouvoir consulter les taux actuels, comprendre la différence entre achat et vente, et contacter l’équipe pour confirmer les détails avant de continuer.' }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="values-section">
      <div class="container">
        <div class="section-heading">
          <p class="eyebrow">{{ isEnglish ? 'Values' : 'Valeurs' }}</p>
          <h2>{{ isEnglish ? 'What guides the service' : 'Ce qui guide le service' }}</h2>
        </div>
        <div class="values-list">
          <article class="value-item" v-for="value in values" :key="value.title">
            <span>{{ value.number }}</span>
            <h3>{{ value.title }}</h3>
            <p>{{ value.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="why-section">
      <div class="container why-grid">
        <div>
          <p class="eyebrow">{{ isEnglish ? 'Why choose us' : 'Pourquoi nous choisir' }}</p>
          <h2>{{ isEnglish ? 'A focused service for customers who value clarity.' : 'Un service ciblé pour les clients qui veulent de la clarté.' }}</h2>
        </div>
        <ul>
          <li v-for="item in whyItems" :key="item">{{ item }}</li>
        </ul>
      </div>
    </section>

    <section class="about-contact">
      <div class="container cta-content">
        <h2>{{ isEnglish ? 'Have a question before exchanging?' : 'Une question avant un change ?' }}</h2>
        <p>{{ isEnglish ? 'Contact Nyenyezia Forex to confirm the next step.' : 'Contactez Nyenyezia Forex pour confirmer la prochaine étape.' }}</p>
        <router-link :to="localizedPath('/contact')" class="cta-button">{{ isEnglish ? 'Contact Us' : 'Nous contacter' }}</router-link>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "AboutPage",
  computed: {
    isEnglish() {
      return this.$route.path.startsWith('/en');
    },
    values() {
      return this.isEnglish ? [
        { number: '01', title: 'Clarity', description: 'Rates and service information should be easy to read and understand.' },
        { number: '02', title: 'Reliability', description: 'The team focuses on consistent support and practical follow-up.' },
        { number: '03', title: 'Respect', description: 'Each customer request deserves clear communication and careful handling.' },
      ] : [
        { number: '01', title: 'Clarté', description: 'Les taux et les informations de service doivent être faciles à lire et à comprendre.' },
        { number: '02', title: 'Fiabilité', description: 'L’équipe privilégie un accompagnement constant et un suivi pratique.' },
        { number: '03', title: 'Respect', description: 'Chaque demande client mérite une communication claire et une attention sérieuse.' },
      ];
    },
    whyItems() {
      return this.isEnglish ? [
        'Clear display of client buy and sell rates.',
        'Support for currency exchange and money transfer needs.',
        'Information available in French and English.',
        'A direct contact path before confirming a transaction.',
      ] : [
        'Affichage clair des taux achat et vente client.',
        'Accompagnement pour le change et les transferts d’argent.',
        'Information disponible en français et en anglais.',
        'Contact direct avant de confirmer une opération.',
      ];
    }
  },
  methods: {
    localizedPath(path) {
      if (!this.isEnglish) return path;
      return path === '/' ? '/en' : '/en' + path;
    }
  }
};
</script>

<style scoped>
.about {
  background: #f5f7fb;
  color: #001041;
  font-family: 'Roboto', sans-serif;
}
.about-hero {
  background:
    linear-gradient(90deg, rgba(0,16,65,0.9), rgba(0,16,65,0.58)),
    url('@/assets/chart-8305514_1280.jpg');
  background-position: center;
  background-size: cover;
  color: white;
  padding: 112px 20px 96px;
}
.hero-content,
.container {
  margin: 0 auto;
  max-width: 1120px;
}
.hero-content {
  max-width: 790px;
  text-align: center;
}
.eyebrow {
  color: #c39446;
  font-size: 0.84rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin: 0 0 10px;
  text-transform: uppercase;
}
.about-hero h1 {
  font-size: clamp(2.35rem, 5vw, 4.4rem);
  line-height: 1.08;
  margin: 0;
}
.about-hero p {
  font-size: 1.12rem;
  line-height: 1.65;
  margin: 18px auto 0;
}
.cta-button {
  background-color: #c39446;
  border-radius: 5px;
  color: white;
  display: inline-block;
  font-weight: 700;
  margin-top: 26px;
  padding: 12px 20px;
  text-decoration: none;
}
.intro-section,
.mission-section,
.values-section,
.why-section {
  padding: 76px 20px;
}
.intro-grid,
.why-grid {
  display: grid;
  gap: 42px;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
}
h2 {
  color: #001041;
  font-size: clamp(2rem, 4vw, 2.85rem);
  line-height: 1.15;
  margin: 0;
}
.intro-copy p,
.mission-card p,
.value-item p,
.why-grid li,
.about-contact p {
  color: #4d5a70;
  font-size: 1rem;
  line-height: 1.65;
}
.section-heading {
  margin: 0 auto 34px;
  max-width: 760px;
  text-align: center;
}
.mission-section,
.why-section {
  background: #fff;
}
.mission-card {
  align-items: center;
  background: #f7f9fc;
  border: 1px solid #dfe4ee;
  border-radius: 8px;
  display: grid;
  gap: 28px;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
  overflow: hidden;
}
.mission-card img {
  height: 100%;
  min-height: 300px;
  object-fit: cover;
  width: 100%;
}
.mission-card div {
  padding: 30px 30px 30px 0;
}
.mission-card h3,
.value-item h3 {
  color: #001041;
  margin: 0 0 10px;
}
.values-list {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.value-item {
  background: #fff;
  border: 1px solid #dfe4ee;
  border-radius: 8px;
  box-shadow: 0 16px 38px rgba(0,16,65,0.08);
  padding: 26px;
}
.value-item span {
  color: #c39446;
  display: block;
  font-weight: 900;
  margin-bottom: 14px;
}
.why-grid ul {
  display: grid;
  gap: 12px;
  list-style: none;
  margin: 0;
  padding: 0;
}
.why-grid li {
  background: #f7f9fc;
  border-left: 4px solid #c39446;
  border-radius: 6px;
  padding: 14px 16px;
}
.about-contact {
  background: #001041;
  color: #fff;
  padding: 68px 20px;
  text-align: center;
}
.about-contact h2,
.about-contact p {
  color: #fff;
}
@media (max-width: 820px) {
  .about-hero {
    padding: 88px 18px 74px;
  }
  .intro-grid,
  .why-grid,
  .mission-card,
  .values-list {
    grid-template-columns: 1fr;
  }
  .mission-card div {
    padding: 0 24px 28px;
  }
  .mission-card img {
    min-height: 220px;
  }
  .cta-button {
    text-align: center;
    width: 100%;
  }
}
</style>
